import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { MEDIA_TYPE_VIDEO } from '../../../util/listingMedia';
import { prefersNoAutoplay } from '../../../util/prefersNoAutoplay';
import { propTypes } from '../../../util/types';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  AspectRatioWrapper,
  Button,
  IconClose,
  IconArrowHead,
  ResponsiveImage,
} from '../../../components';

import HeroLoadSpinner, {
  currentHeroAlreadyLoaded,
  stillHasRequestedVariant,
  stillImageKey,
} from '../HeroLoadSpinner';
import { isActiveGallerySlide } from '../isActiveGallerySlide';

// Copied directly from
// `node_modules/react-image-gallery/styles/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ListingImageGallery.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
  // Page + lightbox thumbs are static strips (wrapping grid / flex), not a
  // swipe-pan rail. v2 default swipe translates the thumb container and leaves
  // empty space under the hero on mobile.
  disableThumbnailSwipe: true,
};
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  // We keep the fractions separated as these are given to AspectRatioWrapper
  // which expects separate width and height
  return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : hasDimensions && aspectRatio <= MAX_PORTRAIT_ASPECT_RATIO
    ? { aspectWidth: 4, aspectHeight: 3 }
    : hasDimensions
    ? { aspectWidth: w, aspectHeight: h }
    : { aspectWidth: 1, aspectHeight: 1 };
};

/**
 * The ListingImageGallery component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Array<propTypes.image>} [props.images] - The images (legacy; used when media is absent)
 * @param {Array} [props.media] - Ordered media items (images + videos)
 * @param {Array<string>} props.imageVariants - The image variants
 * @param {Array<string>} props.thumbnailVariants - The thumbnail variants
 * @param {boolean} [props.compact] - Compact listing-page rail gallery
 * @param {Function} [props.onOpenLightbox] - Compact: open dark modal lightbox at index
 * @param {string} [props.openTriggerId] - id for the open control (focus return)
 * @returns {JSX.Element} listing image gallery component
 */
const ListingImageGallery = props => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadedVideoIndexes, setLoadedVideoIndexes] = useState(() => new Set([0]));
  const [loadedStillKeys, setLoadedStillKeys] = useState(() => new Set());
  const galleryRef = useRef(null);
  // react-image-gallery v2 exposes imperative methods on the ref, not a nested
  // imageGallery DOM node — keep a wrapper for video play/pause queries.
  const galleryRootRef = useRef(null);
  const intl = useIntl();
  // Poster-only under Save-Data / 2g / reduced-motion (same rule as listing cards).
  const noAutoplay = prefersNoAutoplay();
  const {
    rootClassName,
    className,
    images,
    media,
    imageVariants,
    thumbnailVariants,
    compact,
    onOpenLightbox,
    openTriggerId,
  } = props;
  const thumbVariants = thumbnailVariants || imageVariants;
  // Depend on the name, not the array. A new array with the same name must not
  // rebuild items — react-image-gallery resets to startIndex when items change.
  const thumbVariantName = thumbVariants?.[0];
  const mediaItems = media || (images || []).map(image => ({ type: 'image', image }));
  // Compact listing page: fixed 4:3 hero (handoff / crop-safe zone).
  const { aspectWidth, aspectHeight } =
    props.forceAspectRatio || getFirstImageAspectRatio(mediaItems[0]?.image, imageVariants[0]);
  // Memoize items: react-image-gallery 2.1.2 resets to startIndex when `items`
  // identity changes. Do not depend on loadedVideoIndexes — lazy src is applied
  // in renderItem from current state.
  const items = useMemo(() => {
    const resolvedMedia = media || (images || []).map(image => ({ type: 'image', image }));
    return resolvedMedia.map((mediaItem, i) => {
      const image = mediaItem.type === MEDIA_TYPE_VIDEO ? null : mediaItem.image;
      return {
        // We will only use the image resource, but react-image-gallery
        // requires the `original` key from each item.
        original: '',
        alt: intl.formatMessage(
          { id: 'ListingImageGallery.imageAltText' },
          { index: i + 1, count: resolvedMedia.length }
        ),
        thumbAlt: intl.formatMessage(
          { id: 'ListingImageGallery.imageThumbnailAltText' },
          { index: i + 1, count: resolvedMedia.length }
        ),
        thumbnail:
          mediaItem.type === MEDIA_TYPE_VIDEO
            ? mediaItem.video.posterUrl
            : image?.attributes?.variants?.[thumbVariantName],
        image,
        media: mediaItem,
        index: i,
      };
    });
  }, [media, images, intl, thumbVariantName]);

  useEffect(() => {
    if (noAutoplay) {
      return;
    }
    setLoadedVideoIndexes(prev => {
      if (prev.has(currentIndex)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(currentIndex);
      return next;
    });
  }, [currentIndex, noAutoplay]);

  useEffect(() => {
    if (noAutoplay) {
      return;
    }
    const root = galleryRootRef.current;
    if (!root) {
      return;
    }
    root.querySelectorAll('video[data-gallery-video="true"]').forEach(video => {
      const slide = video.closest('.image-gallery-slide');
      if (isActiveGallerySlide(slide) && video.src) {
        const playPromise = video.play();
        if (playPromise?.catch) {
          playPromise.catch(() => {});
        }
      } else {
        video.pause();
      }
    });
  }, [currentIndex, items.length, loadedVideoIndexes, noAutoplay]);

  const markStillLoaded = useCallback(key => {
    if (!key) {
      return;
    }
    setLoadedStillKeys(prev => {
      if (prev.has(key)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  const imageSizesMaybe = isFullscreen
    ? {}
    : { sizes: `(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px` };
  const renderItem = item => {
    const isVideo = item.media?.type === MEDIA_TYPE_VIDEO;
    return (
      <AspectRatioWrapper
        width={aspectWidth || 1}
        height={aspectHeight || 1}
        className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
      >
        <div className={css.itemCentering}>
          {isVideo ? (
            <video
              className={classNames(css.itemVideo, {
                [css.itemClickable]: compact && !!onOpenLightbox,
              })}
              // Lazy: the full-size file is ~15x the card variant, so a video on slide 3 of 5
              // must not cost every visitor several MB for something most never scroll to.
              // Save-Data / 2g / reduced-motion: poster only — never attach the full mp4.
              src={
                !noAutoplay && loadedVideoIndexes.has(item.index)
                  ? item.media.video.mp4Url
                  : undefined
              }
              poster={item.media.video.posterUrl}
              loop
              muted
              playsInline
              preload="none"
              data-gallery-video="true"
            />
          ) : item.index === currentIndex ? (
            <ResponsiveImage
              rootClassName={classNames(css.item, {
                [css.itemClickable]: compact && !!onOpenLightbox,
              })}
              image={item.image}
              alt={item.alt}
              variants={imageVariants}
              onLoad={() => markStillLoaded(stillImageKey(item.image) || `index:${item.index}`)}
              onError={() => markStillLoaded(stillImageKey(item.image) || `index:${item.index}`)}
              {...imageSizesMaybe}
            />
          ) : null}
        </div>
      </AspectRatioWrapper>
    );
  };
  const renderThumbInner = item => {
    if (item.media?.type === MEDIA_TYPE_VIDEO) {
      return (
        <div>
          <img className={css.thumb} src={item.media.video.posterUrl} alt={item.thumbAlt} />
        </div>
      );
    }
    return (
      <div>
        <ResponsiveImage
          rootClassName={css.thumb}
          image={item.image}
          alt={item.thumbAlt}
          variants={thumbVariants}
          sizes="88px"
        />
      </div>
    );
  };

  const onScreenChange = isFull => {
    setIsFullscreen(isFull);
  };

  const openLightboxAtCurrent = () => {
    if (!onOpenLightbox) {
      return;
    }
    const index = galleryRef.current?.getCurrentIndex?.() ?? 0;
    onOpenLightbox(index);
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button className={css.navLeft} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" />
        </div>
      </button>
    );
  };
  const renderRightNav = (onClick, disabled) => {
    return (
      <button className={css.navRight} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" />
        </div>
      </button>
    );
  };
  const renderFullscreenButton = (onClick, isFullscreenParam) => {
    // Compact rail: dark Modal lightbox via onOpenLightbox (not browser fullscreen).
    if (compact && onOpenLightbox) {
      if (isFullscreenParam) {
        return <span className={css.fullscreenHidden} />;
      }
      const safeIndex = Math.min(Math.max(currentIndex, 0), Math.max(items.length - 1, 0));
      return (
        <button
          id={openTriggerId}
          type="button"
          className={css.enlargePill}
          onClick={e => {
            e.stopPropagation();
            openLightboxAtCurrent();
          }}
        >
          <svg
            className={css.enlargePillIcon}
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            aria-hidden="true"
          >
            <path d="M3 7h3l2-2h8l2 2h3v13H3z" />
            <circle cx="12" cy="13" r="3.4" />
          </svg>
          <FormattedMessage
            id="ListingImageGallery.enlargePill"
            values={{ index: safeIndex + 1, count: items.length }}
          />
        </button>
      );
    }
    return isFullscreenParam ? (
      <Button
        onClick={onClick}
        rootClassName={css.close}
        title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
      >
        <span className={css.closeText}>
          <FormattedMessage id="ListingImageGallery.closeModal" />
        </span>
        <IconClose rootClassName={css.closeIcon} />
      </Button>
    ) : (
      <button className={css.openFullscreen} onClick={onClick}>
        <FormattedMessage
          id="ListingImageGallery.viewImagesButton"
          values={{ count: items.length }}
        />
      </button>
    );
  };

  // Catch heroes that decoded before hydration, when onLoad will never fire.
  useLayoutEffect(() => {
    const item = items[currentIndex];
    const key = item?.image ? stillImageKey(item.image) || `index:${currentIndex}` : null;
    if (key && currentHeroAlreadyLoaded(galleryRootRef.current)) {
      markStillLoaded(key);
    }
  }, [items, currentIndex, markStillLoaded]);

  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const classes = classNames(rootClassName || css.root, className, {
    [css.compact]: compact,
  });

  // onBeforeSlide fires when the index changes; onSlide waits until after the
  // transition and would delay mounting the incoming still-photo hero.
  const handleBeforeSlide = nextIndex => {
    setCurrentIndex(nextIndex);
  };

  const currentItem = items[currentIndex];
  const currentStillKey = currentItem?.image
    ? stillImageKey(currentItem.image) || `index:${currentIndex}`
    : null;
  const heroLoading =
    !!currentItem?.image &&
    currentItem.media?.type !== MEDIA_TYPE_VIDEO &&
    stillHasRequestedVariant(currentItem.image, imageVariants) &&
    !loadedStillKeys.has(currentStillKey);

  return (
    <div ref={galleryRootRef}>
      <ReactImageGallery
        ref={galleryRef}
        additionalClass={classes}
        items={items}
        renderItem={renderItem}
        renderThumbInner={renderThumbInner}
        onScreenChange={onScreenChange}
        onBeforeSlide={handleBeforeSlide}
        onClick={compact && onOpenLightbox ? openLightboxAtCurrent : undefined}
        renderLeftNav={renderLeftNav}
        renderRightNav={renderRightNav}
        renderFullscreenButton={renderFullscreenButton}
        showFullscreenButton={!compact || !!onOpenLightbox}
        {...IMAGE_GALLERY_OPTIONS}
        showThumbnails={items.length > 1}
      />
      <HeroLoadSpinner active={heroLoading} containerRef={galleryRootRef} />
    </div>
  );
};

export default ListingImageGallery;
